import { StateCreator } from "zustand";
import type { RosterType } from "amazon-chime-sdk-component-library-react";
import type {
  AttendeeDeviceIntent,
  AttendeeVideoState,
  TileState,
  VideoSourceState,
  VideoUIActiveTab,
  ContentShareZoom,
  ScrollPositions,
  MeetingMode,
  Layout,
  ConnectionQuality,
  ReactionEvent,
} from "./video.types";
import {
  Layout as LayoutEnum,
  MeetingMode as MeetingModeEnum,
} from "./video.types";

/**
 * Structured remediation detail for a session error. Set alongside `sessionError`
 * via `setSessionError(message, detail)` so the access-error surface can render
 * a cause-specific title and a bulleted list of remediations. `null` for
 * unstructured errors (fetch / verify / generic).
 */
export type SessionErrorDetail = { title: string; causes: string[] } | null;

// --- State Type ---
export type VideoSliceState = {
  // Session
  isInitialized: boolean;
  userName: string | null;
  roster: RosterType;
  activeSpeakers: string[];
  sessionError: string | null;
  sessionErrorDetail: SessionErrorDetail;

  // Config
  region: string;
  meetingMode: MeetingMode;
  layout: Layout;
  isEchoReductionEnabled: boolean;
  enableSimulcast: boolean;
  keepLastFrameWhenPaused: boolean;
  isBackgroundBlurEnabled: boolean;
  isNoiseReductionEnabled: boolean;

  // UI
  isSidebarOpen: boolean;
  activeTab: VideoUIActiveTab;
  contentShareZoom: ContentShareZoom;
  scrollPositions: ScrollPositions;
  /** Slug stack for inline article navigation in the materials tab. Empty = original meeting article. */
  materialsArticleStack: string[];

  // Connection
  connectionQuality: ConnectionQuality;
  isReconnecting: boolean;

  // Tile Grid
  attendeeTileState: Record<string, TileState>;
  attendeeStates: Record<string, AttendeeVideoState>;
  attendeeDeviceIntent: Record<string, AttendeeDeviceIntent>;
  videoSourceState: VideoSourceState;

  // Reactions (ephemeral — peer-to-peer emoji reactions anchored per attendee)
  attendeeReactions: Record<string, ReactionEvent[]>;

  // Devices (selected IDs synced from DevicesProvider)
  selectedAudioInputDevice: string | null;
  selectedVideoInputDevice: string | null;
  selectedAudioOutputDevice: string | null;
};

// --- Actions Type ---
export type VideoSliceActions = {
  // Session
  setInitialized: (initialized: boolean) => void;
  setUserName: (name: string | null) => void;
  setRoster: (roster: RosterType) => void;
  setActiveSpeakers: (speakers: string[]) => void;
  /**
   * Atomically write `sessionError` and `sessionErrorDetail`. The single-arg
   * form `setSessionError("msg")` clears `sessionErrorDetail` to `null`, which
   * prevents stale remediation steps from a prior device failure attaching to
   * an unrelated future error. Pass an explicit `detail` to surface structured
   * guidance (`title` + `causes`) on the access-error component.
   */
  setSessionError: (error: string | null, detail?: SessionErrorDetail) => void;

  // Config
  setRegion: (region: string) => void;
  setMeetingMode: (mode: MeetingMode) => void;
  setLayout: (layout: Layout) => void;
  toggleEchoReduction: () => void;
  toggleSimulcast: () => void;
  toggleKeepLastFrameWhenPaused: () => void;
  setBackgroundBlurEnabled: (enabled: boolean) => void;
  setNoiseReductionEnabled: (enabled: boolean) => void;

  // UI
  openSidebar: (tab?: VideoUIActiveTab) => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  setActiveTab: (tab: VideoUIActiveTab) => void;
  setContentShareZoom: (zoom: Partial<ContentShareZoom>) => void;
  resetContentShareZoom: () => void;
  setScrollPosition: (tab: VideoUIActiveTab, position: number) => void;
  pushMaterialsArticle: (slug: string) => void;
  popMaterialsArticle: () => void;
  clearMaterialsArticleStack: () => void;

  // Connection
  setConnectionQuality: (quality: ConnectionQuality) => void;
  setReconnecting: (reconnecting: boolean) => void;

  // Tile Grid — new state machine
  setAttendeeTileState: (attendeeId: string, state: TileState) => void;
  setAttendeeTileStates: (states: Record<string, TileState>) => void;
  removeAttendeeTileState: (attendeeId: string) => void;

  // Tile Grid — legacy (kept during migration)
  setAttendeeStates: (states: Record<string, AttendeeVideoState>) => void;
  updateAttendeeState: (attendeeId: string, update: Partial<AttendeeVideoState>) => void;
  setAttendeeDeviceIntent: (attendeeId: string, intent: AttendeeDeviceIntent) => void;
  clearAttendeeDeviceIntent: (attendeeId: string) => void;
  setVideoSourceState: (state: VideoSourceState) => void;

  // Reactions
  addReaction: (attendeeId: string, reaction: ReactionEvent) => void;
  removeReaction: (attendeeId: string, reactionId: string) => void;

  // Devices
  setSelectedAudioInputDevice: (deviceId: string | null) => void;
  setSelectedVideoInputDevice: (deviceId: string | null) => void;
  setSelectedAudioOutputDevice: (deviceId: string | null) => void;

  // Reset
  resetVideo: () => void;
};

// --- Combined Type ---
export type VideoSlice = VideoSliceState & VideoSliceActions;

// --- Initial State ---
export const initialVideoState: VideoSliceState = {
  // Session
  isInitialized: false,
  userName: null,
  roster: {},
  activeSpeakers: [],
  sessionError: null,
  sessionErrorDetail: null,

  // Config
  region: "",
  meetingMode: MeetingModeEnum.Attendee,
  layout: LayoutEnum.Gallery,
  isEchoReductionEnabled: true,
  enableSimulcast: true,
  keepLastFrameWhenPaused: true,
  isBackgroundBlurEnabled: false,
  isNoiseReductionEnabled: true,

  // UI
  isSidebarOpen: false,
  activeTab: "roster",
  contentShareZoom: { scale: 1, translateX: 0, translateY: 0, isZoomed: false },
  scrollPositions: { chat: 0, roster: 0, materials: 0 },
  materialsArticleStack: [],

  // Connection
  connectionQuality: 'good',
  isReconnecting: false,

  // Tile Grid
  attendeeTileState: {},
  attendeeStates: {},
  attendeeDeviceIntent: {},
  videoSourceState: {
    cameraSources: [],
    activeSpeakersWithCameraSource: [],
    contentShareId: null,
    hasLocalVideo: false,
    hasLocalContentSharing: false,
  },

  // Reactions
  attendeeReactions: {},

  // Devices
  selectedAudioInputDevice: null,
  selectedVideoInputDevice: null,
  selectedAudioOutputDevice: null,
};

// --- Slice Creator ---
export const createVideoSlice: StateCreator<VideoSlice, [], [], VideoSlice> = (
  set
) => ({
  ...initialVideoState,

  // Session
  setInitialized: (initialized) => set({ isInitialized: initialized }),
  setUserName: (name) => set({ userName: name }),
  setRoster: (roster) => set({ roster }),
  setActiveSpeakers: (speakers) => set({ activeSpeakers: speakers }),
  setSessionError: (error, detail = null) =>
    set({ sessionError: error, sessionErrorDetail: detail }),

  // Config
  setRegion: (region) => set({ region }),
  setMeetingMode: (mode) => set({ meetingMode: mode }),
  setLayout: (layout) => set({ layout }),
  toggleEchoReduction: () =>
    set((s) => ({ isEchoReductionEnabled: !s.isEchoReductionEnabled })),
  toggleSimulcast: () => set((s) => ({ enableSimulcast: !s.enableSimulcast })),
  toggleKeepLastFrameWhenPaused: () =>
    set((s) => ({ keepLastFrameWhenPaused: !s.keepLastFrameWhenPaused })),
  setBackgroundBlurEnabled: (enabled) => set({ isBackgroundBlurEnabled: enabled }),
  setNoiseReductionEnabled: (enabled) => set({ isNoiseReductionEnabled: enabled }),

  // UI
  openSidebar: (tab) =>
    set({ isSidebarOpen: true, activeTab: tab ?? "chat" }),
  closeSidebar: () => set({ isSidebarOpen: false }),
  toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen })),
  setActiveTab: (tab) => set({ activeTab: tab }),
  setContentShareZoom: (zoom) =>
    set((s) => {
      const next = {
        scale: zoom.scale ?? s.contentShareZoom.scale,
        translateX: zoom.translateX ?? s.contentShareZoom.translateX,
        translateY: zoom.translateY ?? s.contentShareZoom.translateY,
        isZoomed: (zoom.scale ?? s.contentShareZoom.scale) !== 1,
      };
      return { contentShareZoom: next };
    }),
  resetContentShareZoom: () =>
    set({
      contentShareZoom: {
        scale: 1,
        translateX: 0,
        translateY: 0,
        isZoomed: false,
      },
    }),
  setScrollPosition: (tab, position) =>
    set((s) => ({
      scrollPositions: { ...s.scrollPositions, [tab]: position },
    })),
  pushMaterialsArticle: (slug) =>
    set((s) => ({
      materialsArticleStack: [...s.materialsArticleStack, slug],
    })),
  popMaterialsArticle: () =>
    set((s) => ({
      materialsArticleStack: s.materialsArticleStack.slice(0, -1),
    })),
  clearMaterialsArticleStack: () => set({ materialsArticleStack: [] }),

  // Connection
  setConnectionQuality: (quality) => set({ connectionQuality: quality }),
  setReconnecting: (reconnecting) => set({ isReconnecting: reconnecting }),

  // Tile Grid — new state machine
  setAttendeeTileState: (attendeeId, state) =>
    set((s) => ({
      attendeeTileState: { ...s.attendeeTileState, [attendeeId]: state },
    })),
  setAttendeeTileStates: (states) => set({ attendeeTileState: states }),
  removeAttendeeTileState: (attendeeId) =>
    set((s) => {
      const next = { ...s.attendeeTileState };
      delete next[attendeeId];
      return { attendeeTileState: next };
    }),

  // Tile Grid — legacy
  setAttendeeStates: (states) => set({ attendeeStates: states }),
  updateAttendeeState: (attendeeId, update) =>
    set((s) => {
      if (!(attendeeId in s.attendeeStates)) return s;
      return {
        attendeeStates: {
          ...s.attendeeStates,
          [attendeeId]: { ...s.attendeeStates[attendeeId], ...update },
        },
      };
    }),
  setAttendeeDeviceIntent: (attendeeId, intent) =>
    set((s) => ({
      attendeeDeviceIntent: { ...s.attendeeDeviceIntent, [attendeeId]: intent },
    })),
  clearAttendeeDeviceIntent: (attendeeId) =>
    set((s) => {
      const next = { ...s.attendeeDeviceIntent };
      delete next[attendeeId];
      return { attendeeDeviceIntent: next };
    }),
  setVideoSourceState: (state) => set({ videoSourceState: state }),

  // Reactions
  addReaction: (attendeeId, reaction) =>
    set((s) => {
      const existing = s.attendeeReactions[attendeeId] ?? [];
      // Idempotent on duplicate id (e.g. message delivered twice)
      if (existing.some((r) => r.id === reaction.id)) return s;
      return {
        attendeeReactions: {
          ...s.attendeeReactions,
          [attendeeId]: [...existing, reaction],
        },
      };
    }),
  removeReaction: (attendeeId, reactionId) =>
    set((s) => {
      const existing = s.attendeeReactions[attendeeId];
      if (!existing) return s;
      const filtered = existing.filter((r) => r.id !== reactionId);
      if (filtered.length === existing.length) return s;
      const next = { ...s.attendeeReactions };
      if (filtered.length === 0) {
        delete next[attendeeId];
      } else {
        next[attendeeId] = filtered;
      }
      return { attendeeReactions: next };
    }),

  // Devices
  setSelectedAudioInputDevice: (deviceId) => set({ selectedAudioInputDevice: deviceId }),
  setSelectedVideoInputDevice: (deviceId) => set({ selectedVideoInputDevice: deviceId }),
  setSelectedAudioOutputDevice: (deviceId) => set({ selectedAudioOutputDevice: deviceId }),

  // Reset
  resetVideo: () => set(initialVideoState),
});

// --- Selectors ---
// NOTE: These selectors return actions alongside state, which causes unnecessary
// re-renders. Consumers should use `useShallow` from 'zustand/react/shallow'
// when subscribing to these selectors (e.g. usePlatformStore(useShallow(video.session))).
export const video = {
  session: (s: VideoSlice) => ({
    isInitialized: s.isInitialized,
    userName: s.userName,
    roster: s.roster,
    activeSpeakers: s.activeSpeakers,
    sessionError: s.sessionError,
    sessionErrorDetail: s.sessionErrorDetail,
    setInitialized: s.setInitialized,
    setUserName: s.setUserName,
    setRoster: s.setRoster,
    setActiveSpeakers: s.setActiveSpeakers,
    setSessionError: s.setSessionError,
    connectionQuality: s.connectionQuality,
    setConnectionQuality: s.setConnectionQuality,
    isReconnecting: s.isReconnecting,
    setReconnecting: s.setReconnecting,
  }),
  config: (s: VideoSlice) => ({
    region: s.region,
    meetingMode: s.meetingMode,
    layout: s.layout,
    isEchoReductionEnabled: s.isEchoReductionEnabled,
    enableSimulcast: s.enableSimulcast,
    keepLastFrameWhenPaused: s.keepLastFrameWhenPaused,
    isBackgroundBlurEnabled: s.isBackgroundBlurEnabled,
    isNoiseReductionEnabled: s.isNoiseReductionEnabled,
    setRegion: s.setRegion,
    setMeetingMode: s.setMeetingMode,
    setLayout: s.setLayout,
    toggleEchoReduction: s.toggleEchoReduction,
    toggleSimulcast: s.toggleSimulcast,
    toggleKeepLastFrameWhenPaused: s.toggleKeepLastFrameWhenPaused,
    setBackgroundBlurEnabled: s.setBackgroundBlurEnabled,
    setNoiseReductionEnabled: s.setNoiseReductionEnabled,
  }),
  ui: (s: VideoSlice) => ({
    isSidebarOpen: s.isSidebarOpen,
    activeTab: s.activeTab,
    scrollPositions: s.scrollPositions,
    openSidebar: s.openSidebar,
    closeSidebar: s.closeSidebar,
    toggleSidebar: s.toggleSidebar,
    setActiveTab: s.setActiveTab,
    setScrollPosition: s.setScrollPosition,
  }),
  materialsNavigation: (s: VideoSlice) => ({
    stack: s.materialsArticleStack,
    push: s.pushMaterialsArticle,
    pop: s.popMaterialsArticle,
    clear: s.clearMaterialsArticleStack,
  }),
  zoom: (s: VideoSlice) => ({
    ...s.contentShareZoom,
    setContentShareZoom: s.setContentShareZoom,
    resetContentShareZoom: s.resetContentShareZoom,
  }),
  devices: (s: VideoSlice) => ({
    selectedAudioInputDevice: s.selectedAudioInputDevice,
    selectedVideoInputDevice: s.selectedVideoInputDevice,
    selectedAudioOutputDevice: s.selectedAudioOutputDevice,
    setSelectedAudioInputDevice: s.setSelectedAudioInputDevice,
    setSelectedVideoInputDevice: s.setSelectedVideoInputDevice,
    setSelectedAudioOutputDevice: s.setSelectedAudioOutputDevice,
  }),
  tileGrid: (s: VideoSlice) => ({
    attendeeStates: s.attendeeStates,
    attendeeDeviceIntent: s.attendeeDeviceIntent,
    videoSourceState: s.videoSourceState,
    setAttendeeStates: s.setAttendeeStates,
    updateAttendeeState: s.updateAttendeeState,
    setAttendeeDeviceIntent: s.setAttendeeDeviceIntent,
    clearAttendeeDeviceIntent: s.clearAttendeeDeviceIntent,
    setVideoSourceState: s.setVideoSourceState,
  }),
  reactions: (s: VideoSlice) => ({
    attendeeReactions: s.attendeeReactions,
    addReaction: s.addReaction,
    removeReaction: s.removeReaction,
  }),
  error: (s: VideoSlice) => ({
    session: s.sessionError,
    detail: s.sessionErrorDetail,
    setError: s.setSessionError,
    clearError: () => s.setSessionError(null, null),
  }),
};
